import type { AxiosError } from 'axios'
import { useIntl } from 'react-intl'

import { showSnackbar } from '@b-stock/fe-core/bstock-react'

import { isAxiosError } from '@/buyer-portal/components/ErrorBanner'

import { useBidFormContext } from '../BidFormContext'

type AxiosErrorType = {
  errorMessage: string
}

const ns = 'Auction.bidConfirm.errors'

const getBidAxiosErrorMessageId = (error: AxiosError<AxiosErrorType>) => {
  const status = error.response?.status
  const message = error.response?.data.errorMessage ?? ''

  if (status === 400) {
    if (message === 'no shipment quote selected') {
      return `${ns}.400.noQuoteSelected`
    } else if (message === 'amount is below minimum') {
      return `${ns}.400.amountTooLow`
    } else {
      return `${ns}.400.amountTooHigh`
    }
  } else if (status === 401) {
    return `${ns}.401`
  } else if (status === 403) {
    if (message === 'auction has not opened yet') {
      return `${ns}.403.notStarted`
    } else {
      return `${ns}.403.ended`
    }
  } else if (status === 408) {
    return `${ns}.408`
  } else if (status === 409) {
    return `${ns}.409`
  } else if (status === 429) {
    return `${ns}.429`
  }

  return undefined
}

export const getErrorMessage = (
  error: unknown,
  formatMessage: (descriptor: { id: string }) => string
): string => {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<AxiosErrorType>
    const messageId = getBidAxiosErrorMessageId(axiosError)
    return messageId
      ? formatMessage({ id: messageId })
      : JSON.stringify(axiosError.response?.data)
  }

  return typeof error === 'string'
    ? error
    : typeof error === 'object' && error !== null && 'message' in error
      ? JSON.stringify(error.message)
      : formatMessage({ id: 'Common.error.somethingWentWrong' })
}

export const useOnBidError = () => {
  const { formatMessage } = useIntl()
  const { errorTooltip, setErrorTooltipMessage } = useBidFormContext()

  const onBidError = (error: unknown): string => {
    const message = getErrorMessage(error, formatMessage)

    if (errorTooltip?.referenceElement) {
      setErrorTooltipMessage(message)
    } else {
      showSnackbar({
        content: message,
        options: { autoClose: true, type: 'error' },
      })
    }

    return message
  }

  return onBidError
}
